import type { ComponentProps } from "react";
import { createContext, useContext } from "react";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import { Heading } from "@/shared/ui/common/heading";

const showcaseCardVariants = tv({
  slots: {
    root: "flex w-full flex-col gap-5 overflow-hidden rounded-lg border border-secondary-100 bg-white p-5 text-start transition-all duration-150",
    header: "flex flex-col gap-1",
    description: "text-sm text-neutral-800",
    content: "",
    footer: "",
    list: "list-inside list-disc pl-1.5 text-left text-sm [&_em]:text-neutral-600 [&_em]:not-italic",
    inset: "-m-5 mb-0",
    thumbnail: "size-full h-60 bg-white object-cover sm:h-72",
    badges: "flex flex-wrap items-center gap-2",
  },
  variants: {
    disabled: {
      true: {
        root: "",
        header: "opacity-70",
        content: "opacity-70",
        description: "opacity-70",
        thumbnail: "opacity-70",
      },
      false: {
        root: "cursor-pointer hover:border-secondary-200 hover:shadow-sm [&_*]:cursor-pointer",
      },
    },
  },
});

const insetVariants = tv({
  variants: {
    side: {
      all: "-m-5",
      x: "-mx-5",
      y: "-my-5",
      top: "-mt-5",
      bottom: "-mb-5",
      left: "-ml-5",
      right: "-mr-5",
    },
    bottomSpacing: {
      true: "mb-0",
    },
  },
  defaultVariants: {
    side: "all",
    bottomSpacing: true,
  },
});
type InsetVariants = VariantProps<typeof insetVariants>;

const { root, header, description, content, footer, list, thumbnail, badges } =
  showcaseCardVariants();

type ShowcaseCardRootProps = ComponentProps<"label"> & {
  disabled?: boolean;
};

const ShowcaseCardContext = createContext<ShowcaseCardRootProps | null>(null);

const useShowcaseCardContext = () => {
  return useContext(ShowcaseCardContext) ?? { disabled: false };
};

//TODO: add rendering logic (asCheckable) so we can choose label or div
const Root = ({
  className,
  children,
  disabled,
  ...props
}: ShowcaseCardRootProps) => {
  return (
    <ShowcaseCardContext.Provider value={{ disabled }}>
      <label className={root({ className, disabled })} {...props}>
        {children}
      </label>
    </ShowcaseCardContext.Provider>
  );
};
Root.displayName = "ShowcaseCard.Root";

const Header = ({ className, ...props }: ComponentProps<"div">) => {
  const { disabled } = useShowcaseCardContext();
  return <div className={header({ className, disabled })} {...props} />;
};
Header.displayName = "ShowcaseCard.Header";

const Title = ({
  level = 2,
  ...props
}: Omit<ComponentProps<typeof Heading>, "size">) => (
  <Heading
    data-slot="showcase-card-title"
    level={level}
    size="x-small"
    {...props}
  />
);
Title.displayName = "ShowcaseCard.Title";

const Description = ({ className, ...props }: ComponentProps<"div">) => {
  const { disabled } = useShowcaseCardContext();
  return (
    <div
      data-slot="showcase-card-description"
      className={description({ className, disabled })}
      {...props}
    />
  );
};
Description.displayName = "ShowcaseCard.Description";

const Content = ({ className, ...props }: ComponentProps<"div">) => {
  const { disabled } = useShowcaseCardContext();
  return <div className={content({ className, disabled })} {...props} />;
};
Content.displayName = "ShowcaseCard.Content";

const Footer = ({ className, ...props }: ComponentProps<"div">) => (
  <div className={footer({ className })} {...props} />
);
Footer.displayName = "ShowcaseCard.Footer";

const List = ({ className, ...props }: ComponentProps<"ul">) => {
  return <ul className={list({ className })} {...props} />;
};
List.displayName = "ShowcaseCard.List";

const Inset = ({
  className,
  side,
  ...props
}: ComponentProps<"div"> & InsetVariants) => (
  <div className={insetVariants({ side, className })} {...props} />
);
Inset.displayName = "ShowcaseCard.Inset";

const Thumbnail = ({ alt, className, ...props }: ComponentProps<"img">) => {
  const { disabled } = useShowcaseCardContext();
  return (
    <img
      alt={alt}
      className={thumbnail({ className, disabled })}
      draggable="false"
      {...props}
    />
  );
};
Thumbnail.displayName = "ShowcaseCard.Thumbnail";

const Badges = ({ className, ...props }: ComponentProps<"div">) => {
  return <div className={badges({ className })} {...props} />;
};
Badges.displayName = "ShowcaseCard.Badges";

export const ShowcaseCard = {
  Root,
  Header,
  Title,
  Description,
  Content,
  Footer,
  List,
  Inset,
  Thumbnail,
  Badges,
};
